import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Layout from "@/components/Layout";
import { ArrowLeft, Calendar as CalendarIcon, Download } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday } from "date-fns";
import html2canvas from "html2canvas";
import { toast } from "sonner";

interface AttendanceRecord {
  date: string;
  status: string;
  student_id?: string;
  instructor_id?: string;
}

const AttendanceCalendar = () => {
  const navigate = useNavigate();
  const calendarRef = useRef<HTMLDivElement>(null);
  const [userName, setUserName] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [attendanceType, setAttendanceType] = useState<"student" | "instructor">("student");
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        setUserName(profile.full_name);
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    fetchAttendance();
  }, [currentDate, attendanceType]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const start = format(startOfMonth(currentDate), "yyyy-MM-dd");
      const end = format(endOfMonth(currentDate), "yyyy-MM-dd");

      const table = attendanceType === "student" ? "student_attendance" : "instructor_attendance";
      const { data, error } = await supabase
        .from(table)
        .select("*")
        .gte("date", start)
        .lte("date", end);

      if (error) throw error;
      setAttendanceData(data || []);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceForDate = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return attendanceData.filter((record) => record.date === dateStr);
  };

  const getDateColor = (date: Date) => {
    const records = getAttendanceForDate(date);
    if (records.length === 0) return "bg-background";
    
    const presentCount = records.filter((r) => r.status === "Present").length;
    const absentCount = records.filter((r) => r.status === "Absent").length;
    
    if (presentCount > absentCount) return "bg-green-500 text-white";
    if (absentCount > presentCount) return "bg-red-500 text-white";
    return "bg-yellow-500 text-white";
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = monthStart.getDay();

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDownload = async () => {
    if (!calendarRef.current) return;

    try {
      const canvas = await html2canvas(calendarRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
      });

      const link = document.createElement("a");
      link.download = `attendance-${format(currentDate, "MMMM-yyyy")}.png`;
      link.href = canvas.toDataURL();
      link.click();

      toast.success("Calendar downloaded!");
    } catch (error) {
      toast.error("Failed to download calendar");
    }
  };

  return (
    <Layout userName={userName}>
      <div className="max-w-6xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                <CalendarIcon className="h-6 w-6" />
                Attendance Calendar
              </CardTitle>
              <div className="flex gap-2 w-full sm:w-auto">
                <Select
                  value={attendanceType}
                  onValueChange={(value: "student" | "instructor") => setAttendanceType(value)}
                >
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student Attendance</SelectItem>
                    <SelectItem value="instructor">Instructor Attendance</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleDownload} size="sm" variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent ref={calendarRef}>
            <div className="mb-6 flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={previousMonth}>
                Previous
              </Button>
              <h3 className="text-lg sm:text-xl font-semibold">
                {format(currentDate, "MMMM yyyy")}
              </h3>
              <Button variant="outline" size="sm" onClick={nextMonth}>
                Next
              </Button>
            </div>

            {loading ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-6">
                  <Skeleton className="h-8 w-24" />
                  <Skeleton className="h-8 w-32" />
                  <Skeleton className="h-8 w-24" />
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {[...Array(35)].map((_, i) => (
                    <Skeleton key={i} className="aspect-square rounded-lg" />
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs sm:text-sm font-semibold text-muted-foreground p-2"
                    >
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1 sm:gap-2">
                  {/* Empty cells for days before month starts */}
                  {Array.from({ length: firstDayOfWeek }).map((_, index) => (
                    <div key={`empty-${index}`} className="aspect-square" />
                  ))}
                  
                  {/* Days of the month */}
                  {daysInMonth.map((date) => {
                    const records = getAttendanceForDate(date);
                    const presentCount = records.filter((r) => r.status === "Present").length;
                    const absentCount = records.filter((r) => r.status === "Absent").length;
                    
                    return (
                      <div
                        key={date.toISOString()}
                        className={`
                          aspect-square p-1 sm:p-2 rounded-lg border text-center cursor-pointer
                          transition-all hover:shadow-md
                          ${getDateColor(date)}
                          ${isToday(date) ? "ring-2 ring-primary" : ""}
                        `}
                        title={`${presentCount} present, ${absentCount} absent - Click to view details`}
                        onClick={() => navigate(`/attendance/details?date=${format(date, "yyyy-MM-dd")}&type=${attendanceType}`)}
                      >
                        <div className="text-xs sm:text-sm font-medium">{format(date, "d")}</div>
                        {records.length > 0 && (
                          <div className="text-[10px] mt-1">
                            {presentCount}/{records.length}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 flex flex-wrap gap-4 text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span>Majority Present</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span>Majority Absent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                    <span>Mixed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-background border rounded"></div>
                    <span>No Data</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AttendanceCalendar;
